import { useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { Button } from "../ui/button";
import { StickFigureGuide } from "./StickFigureGuide";
import { detectSubjectBounds } from "../../lib/autoCrop";
import { getCroppedImageBlob } from "../../lib/cropImage";
import { Upload, Wand2, Check, RotateCcw } from "lucide-react";

const OUTPUT_SIZE = 320;

// One frame slot in the Animation Maker: upload -> auto-crop -> optional manual fix -> confirm.
export function FrameCropEditor({ index, guidePose, guideLabel, confirmedUrl, onConfirm, testId }) {
  const [imageSrc, setImageSrc] = useState(null);
  const [autoBox, setAutoBox] = useState(null);
  const [fixing, setFixing] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const src = reader.result;
      setImageSrc(src);
      const box = await detectSubjectBounds(src);
      setAutoBox(box);
      setCroppedAreaPixels(box);
      setFixing(false);
    };
    reader.readAsDataURL(file);
  };

  const confirmCrop = async () => {
    const box = croppedAreaPixels || autoBox;
    const blob = await getCroppedImageBlob(imageSrc, box, OUTPUT_SIZE);
    const url = URL.createObjectURL(blob);
    onConfirm(blob, url);
  };

  const reset = () => {
    setImageSrc(null);
    setAutoBox(null);
    setFixing(false);
    if (fileRef.current) fileRef.current.value = "";
    onConfirm(null, null);
  };

  return (
    <div className="flex items-start gap-2" data-testid={testId}>
      <StickFigureGuide pose={guidePose} label={guideLabel} />
      <div className="flex-1 min-w-0">
        {confirmedUrl ? (
          <div className="relative w-28 h-28 rounded-lg overflow-hidden border-2 border-[#4d7c0f]">
            <img src={confirmedUrl} alt={`Frame ${index + 1}`} className="w-full h-full object-cover" />
            <button data-testid={`${testId}-redo`} onClick={reset} className="absolute top-1 right-1 bg-black/60 rounded-full p-1 text-white hover:text-[#D90429]">
              <RotateCcw className="w-3 h-3" />
            </button>
          </div>
        ) : !imageSrc ? (
          <label className="w-28 h-28 rounded-lg border-2 border-dashed border-white/20 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-[#D4AF37]/60 transition-colors duration-200">
            <Upload className="w-5 h-5 text-[#f1e0c5]/40" />
            <span className="text-[9px] text-[#f1e0c5]/40">Frame {index + 1}</span>
            <input ref={fileRef} data-testid={`${testId}-upload`} type="file" accept="image/*" className="hidden" onChange={onFile} />
          </label>
        ) : fixing ? (
          <div className="space-y-2 w-40">
            <div className="relative w-40 h-40 bg-black/40 rounded-lg overflow-hidden">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="rect"
                initialCroppedAreaPixels={autoBox}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_area, areaPixels) => setCroppedAreaPixels(areaPixels)}
              />
            </div>
            <Button data-testid={`${testId}-confirm-fix`} size="sm" className="w-full rounded-full bg-[#4d7c0f] hover:bg-[#3d6208] text-xs gap-1" onClick={confirmCrop}>
              <Check className="w-3 h-3" /> Use This Crop
            </Button>
          </div>
        ) : (
          <div className="space-y-1.5 w-28">
            <div className="relative w-28 h-28 rounded-lg overflow-hidden border border-white/20">
              <AutoCropPreview imageSrc={imageSrc} box={autoBox} />
            </div>
            <Button data-testid={`${testId}-accept-auto`} size="sm" className="w-full rounded-full bg-[#4d7c0f] hover:bg-[#3d6208] text-[10px] h-7 gap-1" onClick={confirmCrop}>
              <Check className="w-3 h-3" /> Looks Good
            </Button>
            <Button data-testid={`${testId}-fix`} size="sm" variant="secondary" className="w-full rounded-full text-[10px] h-7 gap-1" onClick={() => setFixing(true)}>
              <Wand2 className="w-3 h-3" /> Fix Crop
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function AutoCropPreview({ imageSrc, box }) {
  if (!box) return <img src={imageSrc} alt="" className="w-full h-full object-cover" />;
  return (
    <div
      className="w-full h-full bg-no-repeat"
      style={{
        backgroundImage: `url(${imageSrc})`,
        backgroundPosition: `-${box.x * (112 / box.width)}px -${box.y * (112 / box.height)}px`,
        backgroundSize: `${(box.width ? 112 / box.width : 1) * 10000}% auto`,
      }}
    />
  );
}
